// Admin console: Login Google (/admin/login-google, phase 4). The OAuth
// client the real "Lanjutkan dengan Google" uses.
//   status     plain spacing   active or what is missing, and which source is active
//   warning    notice          the server cannot encrypt the secret (no encryption key)
//   form       card            Client ID, Client Secret (stored: masked, Ganti / Hapus;
//                              never shown), Redirect URI (prefilled with the suggestion)
//   redirect   card            the Authorized redirect URI to copy into Google Cloud
//                              Console, and the scopes the client must allow
//   test       card            Tes konfigurasi and its result
//   audit      rows            who changed which field, when (never values)
import { useEffect, useState, type FormEvent } from "react";
import * as API from "../api";
import { formatDateTime } from "../lib/format";
import { Badge, Field, fieldAria, Notice, PageHeader, PasswordInput, SectionHead, SkeletonRows, useAsync, useTitle } from "../ui/components";
import { Glyph } from "../ui/icons";

export const CLIENT_ID_PATTERN = /^[A-Za-z0-9-]+\.apps\.googleusercontent\.com$/;
export const DEFAULT_SCOPES = ["openid", "email", "profile", "https://www.googleapis.com/auth/drive.file"];

const FIELD_LABEL: Record<string, string> = { clientId: "Client ID", clientSecret: "Client Secret", redirectUri: "Redirect URI" };
const MISSING_LABEL: Record<string, string> = {
  clientId: "Client ID",
  clientSecret: "Client Secret",
  redirectUri: "Redirect URI",
  encryption: "kunci enkripsi server",
};
const SOURCE_LABEL: Record<string, string> = { admin: "pengaturan admin", env: "variabel lingkungan server (env)", none: "belum ada" };

export interface LoginGoogleErrors {
  clientId?: string;
  clientSecret?: string;
  redirectUri?: string;
}

export function validateLoginGoogle(clientId: string, secret: string | null, redirectUri: string): LoginGoogleErrors {
  const e: LoginGoogleErrors = {};
  const id = clientId.trim();
  if (id && !CLIENT_ID_PATTERN.test(id)) e.clientId = "Format Client ID belum benar, contoh 1234-abc.apps.googleusercontent.com.";
  if (secret !== null && /\s/.test(secret.trim())) e.clientSecret = "Client Secret tidak boleh berisi spasi.";
  if (secret !== null && !secret.trim()) e.clientSecret = "Isi Client Secret yang baru, atau batalkan.";
  const uri = redirectUri.trim();
  if (uri) {
    try {
      const u = new URL(uri);
      if (u.protocol !== "https:" && u.hostname !== "localhost" && u.hostname !== "127.0.0.1") e.redirectUri = "Redirect URI harus memakai https.";
    } catch {
      e.redirectUri = "Redirect URI belum berupa alamat yang benar.";
    }
  }
  return e;
}

export function statusLine(s: API.LoginGoogleSettings): string {
  if (s.status.enabled) return "Login Google aktif";
  const missing = s.status.missing.map((m) => MISSING_LABEL[m] ?? m);
  return missing.length ? `Login Google belum aktif: ${missing.join(", ")} belum diisi` : "Login Google belum aktif";
}

const SERVER_ERROR: Record<string, keyof LoginGoogleErrors> = { invalid_client_id: "clientId", invalid_redirect_uri: "redirectUri" };

export function LoginGoogleScreen() {
  useTitle("Login Google");
  const settings = useAsync(() => API.getLoginGoogle(), []);
  const audit = useAsync(() => API.listLoginGoogleAudit(50), []);
  const s = settings.data;

  return (
    <>
      <PageHeader divided title="Login Google" lead="Klien OAuth Google untuk tombol Lanjutkan dengan Google dan akses Google Drive pengguna." />
      {settings.loading && !s ? (
        <SkeletonRows count={4} label="Memuat pengaturan" />
      ) : settings.error ? (
        <Notice tone="danger" title="Pengaturan belum dapat dimuat" action={<button type="button" className="btn btn-secondary" onClick={settings.reload}>Muat ulang</button>}>
          <p>{API.errorMessage(settings.error)}</p>
        </Notice>
      ) : s ? (
        <>
          <section className="region" aria-label="Status Login Google">
            <p className="quiet-line" data-login-status={s.status.enabled ? "on" : "off"}>
              <Glyph name={s.status.enabled ? "tickCircle" : "infoCircle"} />
              <span>
                <strong>{statusLine(s)}</strong>. Sumber yang dipakai: {SOURCE_LABEL[s.status.source] ?? s.status.source}.
                {s.updatedAt ? ` Terakhir diubah ${formatDateTime(s.updatedAt)}${s.updatedBy ? ` oleh ${s.updatedBy.name}` : ""}.` : ""}
              </span>
            </p>
            {!s.encryptionAvailable ? (
              <Notice tone="warning" title="Kunci enkripsi server belum diatur">
                <p>Client Secret tidak dapat disimpan dari halaman ini sampai server memiliki kunci enkripsi. Hubungi pengelola server.</p>
              </Notice>
            ) : null}
          </section>
          <SettingsForm
            settings={s}
            onSaved={(next) => {
              settings.set(next);
              audit.reload();
            }}
          />
          <RedirectCard settings={s} />
          <TestCard />
        </>
      ) : null}
      <section className="region" aria-labelledby="lg-audit">
        <SectionHead id="lg-audit" title="Riwayat perubahan" lead="Hanya nama kolom yang tercatat, tidak pernah isinya." />
        {audit.loading && !audit.data ? (
          <SkeletonRows count={3} label="Memuat riwayat" />
        ) : audit.error ? (
          <Notice tone="danger" title="Riwayat belum dapat dimuat">
            <p>{API.errorMessage(audit.error)}</p>
          </Notice>
        ) : (audit.data ?? []).length === 0 ? (
          <p className="quiet-line">Belum ada perubahan.</p>
        ) : (
          <ul className="rows" data-audit="">
            {(audit.data ?? []).map((a) => (
              <li key={a.id} className="row">
                <span className="row-main">
                  <span className="row-title">{a.fieldsChanged.map((f) => FIELD_LABEL[f] ?? f).join(", ")}</span>
                  <span className="row-meta">
                    {formatDateTime(a.changedAt)}
                    {a.changedBy ? `, oleh ${a.changedBy.name}` : ""}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

function SettingsForm({ settings, onSaved }: { settings: API.LoginGoogleSettings; onSaved: (s: API.LoginGoogleSettings) => void }) {
  const [clientId, setClientId] = useState(settings.clientId ?? "");
  const [redirectUri, setRedirectUri] = useState(settings.redirectUri ?? settings.suggestedRedirectUri);
  // null: keep the stored secret as it is; a string: the replacement being typed.
  const [secret, setSecret] = useState<string | null>(settings.secret.stored ? null : "");
  const [errors, setErrors] = useState<LoginGoogleErrors>({});
  const [notice, setNotice] = useState<{ tone: "success" | "danger"; title: string; body: string } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setClientId(settings.clientId ?? "");
    setRedirectUri(settings.redirectUri ?? settings.suggestedRedirectUri);
    setSecret(settings.secret.stored ? null : "");
  }, [settings]);

  async function save(input: API.LoginGoogleInput, done: string) {
    setBusy(true);
    setNotice(null);
    try {
      const next = await API.saveLoginGoogle(input);
      onSaved(next);
      setNotice({ tone: "success", title: done, body: statusLine(next) + "." });
    } catch (err) {
      const code = err instanceof API.ApiError && err.body && typeof err.body.code === "string" ? err.body.code : "";
      const field = SERVER_ERROR[code];
      if (field) setErrors({ [field]: API.errorMessage(err) });
      else if (code === "encryption_unavailable") setNotice({ tone: "danger", title: "Client Secret belum tersimpan", body: "Server belum memiliki kunci enkripsi." });
      else setNotice({ tone: "danger", title: "Pengaturan belum tersimpan", body: API.errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    // An empty secret field on a form with no stored secret means "not set yet", not an error.
    const typed = secret !== null && (settings.secret.stored || secret.trim()) ? secret : null;
    const found = validateLoginGoogle(clientId, typed, redirectUri);
    setErrors(found);
    if (Object.keys(found).length) return;
    const input: API.LoginGoogleInput = {};
    if (clientId.trim() !== (settings.clientId ?? "")) input.clientId = clientId.trim();
    if (redirectUri.trim() !== (settings.redirectUri ?? "")) input.redirectUri = redirectUri.trim();
    if (typed) input.clientSecret = typed.trim();
    if (!Object.keys(input).length) {
      setNotice({ tone: "success", title: "Tidak ada perubahan", body: "Semua kolom sama dengan yang tersimpan." });
      return;
    }
    await save(input, "Pengaturan disimpan");
  }

  return (
    <section className="card form-card" aria-labelledby="lg-form">
      <h2 id="lg-form" className="card-title">
        Klien OAuth
      </h2>
      {notice ? (
        <Notice tone={notice.tone} title={notice.title}>
          <p>{notice.body}</p>
        </Notice>
      ) : null}
      <form className="form-stack" noValidate onSubmit={submit}>
        <div className="field-row" data-cols="1">
          <Field id="lg-client-id" label="Client ID" error={errors.clientId} hint={settings.env.clientId ? "Juga diatur lewat env server; nilai di sini didahulukan." : undefined}>
            <input
              {...fieldAria("lg-client-id", errors.clientId)}
              value={clientId}
              spellCheck={false}
              autoComplete="off"
              placeholder="1234-abc.apps.googleusercontent.com"
              onChange={(e) => setClientId(e.currentTarget.value)}
            />
          </Field>
        </div>
        <div className="field-row" data-cols="1">
          {secret === null ? (
            <div className="field" data-secret="stored">
              <span className="field-label">Client Secret</span>
              <p className="quiet-line">
                <Badge tone="success">Tersimpan</Badge>
                <span>{settings.secret.masked ?? "Tersimpan"}</span>
              </p>
              <div className="button-row">
                <button type="button" className="btn btn-secondary" disabled={busy || !settings.encryptionAvailable} onClick={() => setSecret("")}>
                  Ganti
                </button>
                <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => void save({ clearSecret: true }, "Client Secret dihapus")}>
                  Hapus
                </button>
              </div>
            </div>
          ) : (
            <Field id="lg-secret" label="Client Secret" error={errors.clientSecret} hint="Disimpan terenkripsi dan tidak pernah ditampilkan lagi.">
              <PasswordInput
                {...fieldAria("lg-secret", errors.clientSecret)}
                value={secret}
                autoComplete="off"
                disabled={!settings.encryptionAvailable}
                onChange={(e) => setSecret(e.currentTarget.value)}
              />
            </Field>
          )}
        </div>
        {secret !== null && settings.secret.stored ? (
          <div className="button-row">
            <button type="button" className="btn btn-ghost" onClick={() => setSecret(null)}>
              Batal ganti secret
            </button>
          </div>
        ) : null}
        <div className="field-row" data-cols="1">
          <Field id="lg-redirect" label="Redirect URI" error={errors.redirectUri} hint={`Saran: ${settings.suggestedRedirectUri}`}>
            <input {...fieldAria("lg-redirect", errors.redirectUri)} value={redirectUri} spellCheck={false} autoComplete="off" onChange={(e) => setRedirectUri(e.currentTarget.value)} />
          </Field>
        </div>
        <div className="button-row">
          <button type="submit" className="btn" aria-busy={busy || undefined}>
            Simpan pengaturan
          </button>
        </div>
      </form>
    </section>
  );
}

function RedirectCard({ settings }: { settings: API.LoginGoogleSettings }) {
  const [copied, setCopied] = useState<string | null>(null);
  const scopes = settings.scopes.length ? settings.scopes : DEFAULT_SCOPES;
  async function copy() {
    try {
      await navigator.clipboard.writeText(settings.effectiveRedirectUri);
      setCopied("URI disalin.");
    } catch {
      setCopied("Salin manual: pilih teks URI di atas.");
    }
  }
  return (
    <section className="card form-card" aria-labelledby="lg-redirect-title">
      <h2 id="lg-redirect-title" className="card-title">
        Authorized redirect URI
      </h2>
      <p className="section-lead">Tambahkan URI ini di Google Cloud Console &gt; APIs &amp; Services &gt; Credentials &gt; OAuth client &gt; Authorized redirect URIs</p>
      <p>
        <code data-effective-uri="">{settings.effectiveRedirectUri}</code>
      </p>
      <div className="button-row">
        <button type="button" className="btn btn-secondary" onClick={copy}>
          Salin URI
        </button>
      </div>
      {copied ? <p role="status">{copied}</p> : null}
      <h3 className="card-title">Scope yang diperlukan</h3>
      <ul data-scopes="">
        {scopes.map((sc) => (
          <li key={sc}>
            <code>{sc}</code>
          </li>
        ))}
      </ul>
    </section>
  );
}

function TestCard() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<API.LoginGoogleTestResult | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  async function run() {
    setBusy(true);
    setFailure(null);
    try {
      setResult(await API.testLoginGoogle());
    } catch (err) {
      setResult(null);
      setFailure(API.errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  const checks: [string, boolean][] = result
    ? [
        ["Client ID valid", result.clientIdValid],
        ["Client Secret dapat dipakai", result.secretUsable],
        ["Redirect URI valid", result.redirectUriValid],
        ["Server Google dapat dihubungi", result.discoveryReachable],
      ]
    : [];
  return (
    <section className="card form-card" aria-labelledby="lg-test">
      <h2 id="lg-test" className="card-title">
        Tes konfigurasi
      </h2>
      <div className="button-row">
        <button type="button" className="btn btn-secondary" aria-busy={busy || undefined} onClick={run}>
          Tes konfigurasi
        </button>
      </div>
      {failure ? (
        <Notice tone="danger" title="Tes belum dapat dijalankan">
          <p>{failure}</p>
        </Notice>
      ) : null}
      {result ? (
        <div data-test-result={result.ok ? "ok" : "fail"}>
          <Notice tone={result.ok ? "success" : "warning"} title={result.ok ? "Konfigurasi siap dipakai" : "Konfigurasi belum lengkap"} />
          <ul className="rows">
            {checks.map(([label, ok]) => (
              <li key={label} className="row">
                <span className="row-main">
                  <span className="row-title">{label}</span>
                </span>
                <span className="row-side">{ok ? <Badge tone="success">Ya</Badge> : <Badge tone="danger">Tidak</Badge>}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
